import { jsx as _jsx } from "react/jsx-runtime";
import AskInput from '#pplx/frontend/apps/ask/ask-input/ask-input-core/components/AskInput.js';
import { usePromptHistoryAnalytics } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/usePromptHistoryAnalytics.js';
import { useVoiceModeImplementation } from '#pplx/frontend/apps/ask/ask-input/voice-controls/voiceModeImplementationShared.js';
import { useComputerAskInputLocalMode } from '#pplx/frontend/apps/ask/computer/ask-input/ComputerAskInputLocalMode.js';
import { useComputerAskInput } from '#pplx/frontend/apps/ask/computer/ask-input/useComputerAskInput.js';
import { useComputerMainPromptHistory } from '#pplx/frontend/apps/ask/computer/ask-input/useComputerMainHistory.js';
import { useComputerSearchModelPreference } from '#pplx/frontend/apps/ask/computer/ask-input/useComputerSearchModelPreference.js';
import { useHappyDispatch } from '#pplx/frontend/apps/ask/computer/branding/ComputerAnimatedLockupShared.js';
import { useComputerRemoteBoxExecutionTargetAddon } from '#pplx/frontend/apps/ask/computer/remotebox-execution-target/useComputerRemoteBoxExecutionTargetAddon.js';
import { useComputerVoice } from '#pplx/frontend/apps/ask/computer/voice/state/ComputerVoiceStateContextShared.js';
import { useCanUseComputer } from '#pplx/frontend/apps/ask/hooks/computer-access-hooks/useCanUseComputer.js';
import { useQueryFillParams } from '#pplx/frontend/apps/ask/hooks/query-fill-params-hooks/useQueryFillParams.js';
import { useMentionTypeaheadOptions } from '#pplx/frontend/apps/ask/mention-typeahead/useMentionTypeaheadOptions.js';
import { usePreloadThreadPagesOnIntent } from '#pplx/frontend/apps/ask/page-components/thread-pages-preload/usePreloadThreadPagesOnIntent.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useAskComposerAppearance } from '#pplx/frontend/apps/ask/providers/useAskComposerAppearance.js';
import { usePreferredSearchModels } from '#pplx/frontend/apps/ask/settings/hooks/usePreferredSearchModels.js';
import { clearRemixAssetAttachmentHandoff, getRemixAssetAttachmentHandoff, } from '#pplx/frontend/apps/ask/shared-ui-components/remixAssetAttachments.js';
import { useInputActions, useShouldTriggerFocus, } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { SearchMode, SearchModel } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsUserLoggedIn, useIsUserLoggedInLoading } from '#pplx/frontend/auth/userHooks.js';
import { Chin } from '#pplx/frontend/composer-sdk/components/Chin.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { assetsClient } from '#pplx_generated/typespec/rest/assets/client.js';
import classNames from 'classnames';
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
// Single-file event: defined here at its only call site rather than in a shared package.
const computerCommandMenuOpenedEvent = EVENT_PAYLOAD;
/** Derives a Blob MIME type from the original asset type for remix source content. */
function getRemixBlobMimeType(assetType) {
    switch (assetType) {
        case 'XLSX_FILE':
            return 'text/html';
        case 'DOC_FILE':
            return 'text/plain';
        case 'APP':
        case 'SLIDES':
        default:
            return 'text/html';
    }
}
const DISABLED_SEARCH_MODE_CONTROLS = { enabled: false };
export const ComputerAskInput = React.memo(forwardRef(function ComputerAskInput({ basePath, placeholder, onBeforeNavigate, onSubmit, onDisabledSubmit, wrapperClassName: externalWrapperClassName, compactPadding = false, allowUpsellSubmit = false, dropdownPlacement = 'top', dropdownClassName, enableVoice = false, autosuggestionsEnabled, onFileAttachComplete, focusOnFileAttachComplete, onRemoveFile, renderAttachments, minRows = 2, autofocus, isFollowUp = false, focusOnShouldTriggerFocus, additionalRightAttribution, showVoice, showModelSelector: showModelSelectorOverride, searchModeControls = DISABLED_SEARCH_MODE_CONTROLS, onVoiceClick: onVoiceClickOverride, targetCollection, workflowConfig, hasShadow = true, decorativeGlow, }, forwardedRef) {
    const appearance = useAskComposerAppearance(SearchMode.ASI);
    const canUseComputer = useCanUseComputer();
    const { track } = useInstrumentation();
    const trackPromptHistoryEvent = usePromptHistoryAnalytics({
        track,
        surface: 'computer-main',
    });
    const shouldTriggerFocus = useShouldTriggerFocus();
    const { setShouldTriggerFocus } = useInputActions();
    const { toggleVoice } = useComputerVoice();
    const hostVoice = useVoiceModeImplementation();
    const isUserLoggedIn = useIsUserLoggedIn();
    const isUserLoggedInLoading = useIsUserLoggedInLoading();
    const { setConfiguredModel } = useThreadConfig();
    const { setPreferredSearchModel, clearPreferredSearchModel } = usePreferredSearchModels();
    const { defaultComputerSearchModel, computerSearchModel } = useComputerSearchModelPreference();
    const { onClick: preloadThreadPagesOnClick, onKeyDown: preloadThreadPagesOnKeyDown } = usePreloadThreadPagesOnIntent(SearchMode.ASI);
    const handleSearchModelSelect = useCallback((model) => {
        setPreferredSearchModel(SearchMode.ASI, model);
        setConfiguredModel(model);
    }, [setConfiguredModel, setPreferredSearchModel]);
    const handleSearchModelDeselect = useCallback(() => {
        clearPreferredSearchModel(SearchMode.ASI);
        setConfiguredModel(defaultComputerSearchModel);
    }, [clearPreferredSearchModel, defaultComputerSearchModel, setConfiguredModel]);
    const handleComputerCommandMenuOpen = useCallback((source) => {
        track(computerCommandMenuOpenedEvent({
            name: 'computer command menu opened',
            data: { source },
        }));
    }, [track]);
    const localMode = useComputerAskInputLocalMode();
    const isLocalMode = localMode?.submitSearchModel !== undefined;
    const localSubmissionBlockReason = localMode?.submissionBlockReason;
    const { addon: remoteBoxAddon, workflowConfig: remoteBoxWorkflowConfig } = useComputerRemoteBoxExecutionTargetAddon({
        eligible: !isLocalMode && !isFollowUp,
        mode: { mode: 'new-task', projectUuid: targetCollection?.uuid },
        workflowConfig,
    });
    const [value, json, actions] = useComputerAskInput({
        basePath,
        onBeforeNavigate,
        onSubmit,
        searchModel: localMode?.submitSearchModel ?? computerSearchModel,
        targetCollection,
        disabled: localSubmissionBlockReason !== undefined,
        workflowConfig: remoteBoxWorkflowConfig,
    });
    const { onChange: onPromptHistoryChange, onSubmit: onPromptHistorySubmit, onKeyDown: onPromptHistoryKeyDown, } = useComputerMainPromptHistory({
        value,
        setValue: actions.setUserInput,
        onChange: actions.onChange,
        onSubmit: actions.onSubmit,
        trackEvent: trackPromptHistoryEvent,
    });
    const handleKeyDown = useCallback((event) => {
        preloadThreadPagesOnKeyDown();
        return onPromptHistoryKeyDown(event);
    }, [onPromptHistoryKeyDown, preloadThreadPagesOnKeyDown]);
    useHappyDispatch(true, value);
    const { mentionTypeaheadOptions, mentionIconRenderer, onTriggerTypeahead, onMentionSearchQueryChange, } = useMentionTypeaheadOptions({
        reason: 'computer',
        variants: ['sources', 'skills', 'space', 'tab', 'device', 'user'],
        forceSkillsEnabled: true,
        omitDeviceMentions: remoteBoxAddon !== null,
        useComputerCommandMenu: true,
    });
    const askInputRef = useRef(null);
    const previousIsLocalMode = useRef(isLocalMode);
    useEffect(() => {
        if (previousIsLocalMode.current !== isLocalMode) {
            askInputRef.current?.removeAttachments();
            previousIsLocalMode.current = isLocalMode;
        }
    }, [isLocalMode]);
    const setAskInputRef = useCallback((node) => {
        askInputRef.current = node;
        if (typeof forwardedRef === 'function') {
            forwardedRef(node);
        }
        else if (forwardedRef) {
            forwardedRef.current = node;
        }
    }, [forwardedRef]);
    useEffect(() => {
        if (!focusOnShouldTriggerFocus || !shouldTriggerFocus)
            return;
        askInputRef.current?.focusInput();
        setShouldTriggerFocus(false);
    }, [focusOnShouldTriggerFocus, setShouldTriggerFocus, shouldTriggerFocus]);
    const addAttachmentUrl = useCallback((url, filename) => {
        askInputRef.current?.addAttachmentUrl(url, filename);
    }, []);
    const addRemixAttachment = useCallback(async (remixId) => {
        if (isUserLoggedInLoading || !isUserLoggedIn) {
            return false;
        }
        const handoff = getRemixAssetAttachmentHandoff(remixId);
        if (!handoff) {
            return false;
        }
        const askInput = askInputRef.current;
        if (!askInput) {
            return false;
        }
        // Resolve the source URL and filename before attaching the asset.
        let resolvedUrl;
        let filename = handoff.filename ?? `${handoff.metadata.name}.zip`;
        let revokeAfter = false;
        if (handoff.remixSourceAssetId) {
            const { data, response } = await assetsClient.GET('/rest/assets/{asset_id}/remix-source/metadata', 'computer-remix-source', {
                params: {
                    path: { asset_id: handoff.remixSourceAssetId },
                },
            });
            if (data) {
                resolvedUrl = data.source_url;
                filename = data.filename;
            }
            else if (Number(response.status) === 404) {
                // During a rolling deploy, the frontend can reach a backend that
                // only exposes the redirect endpoint.
                resolvedUrl = `/rest/assets/${encodeURIComponent(handoff.remixSourceAssetId)}/remix-source`;
            }
            else {
                return false;
            }
        }
        else if (handoff.sourceContent) {
            const blobType = getRemixBlobMimeType(handoff.metadata.assetType);
            const blob = new Blob([handoff.sourceContent], { type: blobType });
            resolvedUrl = URL.createObjectURL(blob);
            revokeAfter = true;
        }
        else if (handoff.sourceUrl) {
            resolvedUrl = handoff.sourceUrl;
        }
        if (!resolvedUrl || !filename) {
            return false;
        }
        try {
            await askInput.addMediaFile(resolvedUrl, filename, undefined, {
                ...handoff.metadata,
                handoffId: remixId,
            });
            clearRemixAssetAttachmentHandoff(remixId);
        }
        finally {
            if (revokeAfter) {
                URL.revokeObjectURL(resolvedUrl);
            }
        }
        return true;
    }, [isUserLoggedIn, isUserLoggedInLoading]);
    useQueryFillParams({
        setUserInput: actions.setUserInput,
        addAttachmentUrl: isLocalMode ? undefined : addAttachmentUrl,
        addRemixAttachment: isLocalMode ? undefined : addRemixAttachment,
    });
    const { $t } = useIntl();
    const defaultPlaceholder = $t({
        id: '20fddf486c',
        defaultMessage: 'What should we work on next?',
        description: 'Placeholder text for the computer ask input field',
    });
    return (_jsx(AskInput, { appearance: appearance, ...actions, ref: setAskInputRef, value: value, json: json, onChange: onPromptHistoryChange, onSubmit: onPromptHistorySubmit, onClick: preloadThreadPagesOnClick, onKeyDown: handleKeyDown, querySource: "computer", placeholder: placeholder ?? defaultPlaceholder, autofocus: autofocus ?? canUseComputer, initialLayout: "expanded", minRows: minRows, isFollowUp: isFollowUp, hasShadow: hasShadow, decorativeGlow: decorativeGlow, searchModeControls: searchModeControls, showSources: true, showSkills: true, showTabs: true, showFileUpload: true, showVoice: showVoice ?? hostVoice?.isAvailable ?? enableVoice, showModelSelector: showModelSelectorOverride ?? true, searchModel: computerSearchModel, onSearchModelSelect: handleSearchModelSelect, onSearchModelDeselect: handleSearchModelDeselect, searchModelMenuLocalMode: localMode?.searchModelMenuLocalMode, searchModelTriggerAccessory: localMode?.triggerAccessory, onVoiceClick: onVoiceClickOverride ?? (enableVoice ? toggleVoice : undefined), additionalRightAttribution: additionalRightAttribution, layoutKey: "computer-ask-input", disableSubmission: (!canUseComputer && !allowUpsellSubmit) || localSubmissionBlockReason !== undefined, disabledSubmitTooltip: localSubmissionBlockReason, disableActionButtons: !canUseComputer && !allowUpsellSubmit, dropdownPlacement: dropdownPlacement, dropdownClassName: dropdownClassName, onDisabledSubmit: localSubmissionBlockReason !== undefined ? undefined : onDisabledSubmit, wrapperClassName: classNames(compactPadding && '[&>div:last-child]:!px-3', externalWrapperClassName), dropBehavior: "all-element-only", autosuggestionsEnabled: autosuggestionsEnabled, searchModeOverride: SearchMode.ASI, skipSearchModeGate: allowUpsellSubmit, onFileAttachComplete: onFileAttachComplete, focusOnFileAttachComplete: focusOnFileAttachComplete, onRemoveFile: onRemoveFile, renderAttachments: renderAttachments, mentionTypeaheadOptions: mentionTypeaheadOptions, mentionIconRenderer: mentionIconRenderer, onTriggerTypeahead: onTriggerTypeahead, onMentionSearchQueryChange: onMentionSearchQueryChange, onComputerCommandMenuOpen: handleComputerCommandMenuOpen, bottomComponent: remoteBoxAddon ? _jsx(Chin, { children: remoteBoxAddon }) : undefined, showWorkspaceFolders: !!(import.meta.env.VITE_IS_COMPUTER_APP_BUILD ||
            import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD) }));
}));
//# sourceMappingURL=ComputerAskInput.js.map